import type { PersistedClient, Persister } from "@tanstack/query-persist-client-core";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import type { Query } from "@tanstack/react-query";
import type { PersistQueryClientOptions } from "@tanstack/react-query-persist-client";
import { hoursToMilliseconds } from "date-fns";
import { z } from "zod";

const CACHE_MAX_AGE_MS = hoursToMilliseconds(4);

const CHAT_MESSAGES_QUERY_KEYS = new Set([
    "generalChatMessages",
    "requestChatMessages",
]);

const apiClientQueryKeySchema = z.tuple([
    z.string().min(1),
    z.record(z.string(), z.unknown()),
]);

function getSessionStorage(): Storage | undefined {
    try {
        return sessionStorage;
    } catch {
        return undefined;
    }
}

export function getQueryPersistStorageKey(search = location.search): string {
    const tenant = new URLSearchParams(search).get("tenant") ?? "unknown";
    return `albertine-mini-apps-query-cache:${tenant}`;
}

export function shouldDehydrateQuery(query: Query): boolean {
    return (
        query.state.status === "success" &&
        apiClientQueryKeySchema.safeParse(query.queryKey).success
    );
}

export function stripVolatileChatUnreadIds(client: PersistedClient): PersistedClient {
    return {
        ...client,
        clientState: {
            ...client.clientState,
            queries: client.clientState.queries.map((query) => {
                const queryKey = query.queryKey;
                if (
                    !Array.isArray(queryKey) ||
                    typeof queryKey[0] !== "string" ||
                    !CHAT_MESSAGES_QUERY_KEYS.has(queryKey[0])
                ) {
                    return query;
                }

                const data = query.state.data;
                if (
                    !data ||
                    typeof data !== "object" ||
                    !("pages" in data) ||
                    !Array.isArray(data.pages)
                ) {
                    return query;
                }

                return {
                    ...query,
                    state: {
                        ...query.state,
                        data: {
                            ...data,
                            pages: data.pages.map((page) => ({
                                ...page,
                                unreadMessageIds: [],
                            })),
                        },
                    },
                };
            }),
        },
    };
}

const storagePersister = createSyncStoragePersister({
    storage: getSessionStorage(),
    key: getQueryPersistStorageKey(),
});

export const persister: Persister = {
    persistClient: (client) =>
        storagePersister.persistClient(stripVolatileChatUnreadIds(client)),
    restoreClient: async () => {
        const client = await storagePersister.restoreClient();
        return client ? stripVolatileChatUnreadIds(client) : undefined;
    },
    removeClient: () => storagePersister.removeClient(),
};

export const persistOptions: Omit<PersistQueryClientOptions, "queryClient"> = {
    persister,
    maxAge: CACHE_MAX_AGE_MS,
    buster: import.meta.env.ALBERTINE_VERSION,
    dehydrateOptions: {
        shouldDehydrateQuery,
    },
};
